// A CONTA DO AJUSTE DE ESTOQUE — feita no SALVAR, nunca enquanto ele digita.
//
// Pedido dele, 2026-08-25, sobre o modal `Ajustar estoque` da ficha do produto:
// *"Gostaria que os campos nao fossem auto-calculados. Gostaria que os calculos fossem realizados
// na hora de salvar"* e *"Se houver inconsistencia, exibir mensagem de erro e cancelar o Salvar"*.
//
// ─── O QUE MUDOU DE MODELO ─────────────────────────────────────────────────────────────────────
// Ate aqui o modal tinha UMA fonte (as linhas de prateleira) e tudo o mais era espelho ao vivo:
// digitar o `Valor` empurrava a prateleira marcada tecla a tecla, e digitar na prateleira empurrava
// de volta o `Valor` e ate o botao Adicionar/Diminuir. Agora os dois lados sao DUAS DECLARACOES
// INDEPENDENTES do mesmo total, e quem as concilia e' esta peca, no clique:
//
//   esquerda declara ->  base ± valor          (o quanto entrou/saiu, caindo na prateleira marcada)
//   direita  declara ->  soma das prateleiras  (quanto tem em cada lugar, contado a mao)
//
// ⚠️ E' de proposito que os dois possam se contradizer. Enquanto havia espelhamento a contradicao
// era impossivel por construcao; ao soltar os lados ela passa a existir, e a saida que ELE escolheu
// nao foi eleger um vencedor calado — foi **recusar o Salvar e mostrar os dois numeros**. Sistema
// que escolhe sozinho entre duas ordens do operador acerta as vezes e cria erro silencioso no
// resto, que e' o mesmo argumento do [DA-602] pra distribuicao ser do operador.
//
// ⛔ Negativo NAO e' barrado aqui. Tirar 200 de uma prateleira que tem 50 passa — o pedido dele
// nomeia a inconsistencia ENTRE OS LADOS, e barrar saldo negativo seria uma trava que ele nao
// pediu, num caminho que hoje ja a permite.

/** Uma prateleira como a tela a tem no momento do clique. */
export interface LinhaConta {
  localizacao_id: number;
  /** O que esta ESCRITO no campo agora — texto, porque o campo e' de digitacao livre. */
  quantidade: string;
  /** Quanto tinha quando o modal abriu. E' o que distingue "ele mexeu aqui" de "esta como veio". */
  original: number;
}

export interface EntradaConta {
  /** `estoque_atual` do produto quando o modal abriu. */
  base: number;
  /** 1 = Adicionar, -1 = Diminuir. */
  sinal: 1 | -1;
  /** O que esta escrito no campo Valor. Vazio = a esquerda nao declara nada. */
  valor: string;
  /** Indice da linha marcada — onde o Valor cai quando so a esquerda foi usada. */
  alvo: number;
  linhas: LinhaConta[];
  /** Prateleiras apagadas na sessao do modal, com o saldo que foi junto. */
  apagadas: { original: number }[];
}

export type ResultadoConta =
  | { ok: true; total: number; quantidades: Map<number, number> }
  | { ok: false; esquerda: number; direita: number; diferenca: number };

const arred = (n: number) => Number(n.toFixed(3));

/** `'1.234'` / `'1,234'` / `''` -> numero. Campo vazio e' zero, nunca NaN. */
export const numeroDoCampo = (v: string | number | null | undefined): number => {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

export function calcularAjuste(e: EntradaConta): ResultadoConta {
  const soma = arred(e.linhas.reduce((s, l) => s + numeroDoCampo(l.quantidade), 0));
  const delta = arred(e.sinal * Math.abs(numeroDoCampo(e.valor)));

  const usouEsquerda = delta !== 0;
  // ⚠️ "Tocada" e' o campo ter sido MEXIDO, nao a soma ter mudado. Comparar a soma contra a base
  // daria falso positivo no produto que ja abre incoerente (os que a tela Inconsistencias lista):
  // ele acusaria contradicao sem ninguem ter digitado nada.
  const tocouDireita = e.linhas.some((l) => numeroDoCampo(l.quantidade) !== l.original)
    || e.apagadas.some((a) => a.original !== 0);

  const quantidades = new Map<number, number>();
  const comoEstao = () => {
    for (const l of e.linhas) quantidades.set(l.localizacao_id, arred(numeroDoCampo(l.quantidade)));
    return quantidades;
  };

  // 1. So a direita (ou nada): as prateleiras mandam e o total e' a soma delas. [DA-339]
  if (!usouEsquerda) return { ok: true, total: soma, quantidades: comoEstao() };

  // 2. So a esquerda: o Valor cai AGORA na prateleira marcada — este e' o calculo que saiu de
  //    perto do teclado e veio pro clique.
  if (!tocouDireita) {
    comoEstao();
    const alvo = e.linhas[e.alvo];
    if (alvo) quantidades.set(alvo.localizacao_id, arred(alvo.original + delta));
    return { ok: true, total: arred(e.base + delta), quantidades };
  }

  // 3. Os dois lados falaram. Se dizem o mesmo total, a direita detalha onde; se divergem, recusa.
  const esquerda = arred(e.base + delta);
  if (Math.abs(esquerda - soma) > 0.001) {
    return { ok: false, esquerda, direita: soma, diferenca: arred(Math.abs(esquerda - soma)) };
  }
  return { ok: true, total: soma, quantidades: comoEstao() };
}
